// Returns the unique elements of the input array
export const uniqueOf = <T>(src: T[]): T[] => {
  const seen = new Set<T>();
  src.forEach((x) => {
    if (!seen.has(x)) {
      seen.add(x);
    }
  });

  return Array.from(seen);
};

// Returns the elements of the input array which have a unique output value when inserted into the given function
export const uniqueBy = <T, V>(src: T[], func: (param: T) => V): T[] => {
  const ret: T[] = [];
  const seen = new Set<V>();
  src.forEach((x) => {
    const mapped = func(x);
    if (!seen.has(mapped)) {
      seen.add(mapped);
      ret.push(x);
    }
  });

  return ret;
};

export const sum = (src: number[]): number =>
  src.reduce((curTotal, nextValue) => curTotal + nextValue, 0);

export const sumFields = <T extends Record<string, number>>(
  src: T[]
): Partial<T> => {
  if (src.length === 0) {
    return {};
  }
  const res: T = {} as T;
  src.forEach((x) => {
    Object.keys(x).forEach(
      // @ts-expect-error required as ts doesn't realise T[keyof T] has type number despite extending Record<string, number>
      (key: keyof T) => (res[key] = (res[key] ?? 0) + (x[key] ?? 0))
    );
  });

  return res;
};

export const removeBlank = <T>(src: (T | false | null | undefined)[]): T[] =>
  src.filter((x) => !!x) as any[]; // as required as ts doesn't understand !!x means x is of type T

// Returns the only value in the array or null if the array is empty or has differing values
export const onlyValueOrNull = <T>(src: T[]): T | null => {
  if (src.length === 0) {
    return null;
  }

  const firstValue = src[0];

  return src.every((x) => x === firstValue) ? firstValue : null;
};

export const splitNumber = (num: number, count: number) => {
  const value = Math.floor(num / count);
  const remainder = num % count;

  return Array(count)
    .fill(0)
    .map((_, index) => value + (!index ? remainder : 0));
};

export const fillMissing = <T>(
  src: T[],
  backup: T[],
  by: (x: T) => string
): T[] => {
  const res = [...src];
  const alreadyHas = new Set<string>(src.map((x) => by(x)));
  backup.forEach((x) => {
    if (!alreadyHas.has(by(x))) {
      alreadyHas.add(by(x));
      res.push(x);
    }
  });
  return res;
};

export const count = (records: string[]): Record<string, number> => {
  const ret: Record<string, number> = {};
  records.forEach((x) => {
    ret[x] = (ret[x] || 0) + 1;
  });
  return ret;
};

export const createSetIfNoDuplicates = <T>(x: T[]): Set<T> | null => {
  const ret = new Set<T>();
  for (const value of x) {
    if (ret.has(value)) {
      return null;
    }
    ret.add(value);
  }
  return ret;
};

export const arraysEqualAndNoDuplicates = <T>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  const aSet = createSetIfNoDuplicates(a);
  if (aSet == null) {
    return false;
  }

  const bSet = createSetIfNoDuplicates(b);
  if (bSet == null) {
    return false;
  }

  for (const x of a) {
    if (!bSet.has(x)) {
      return false;
    }
  }
  for (const x of b) {
    if (!aSet.has(x)) {
      return false;
    }
  }

  return true;
};
